import {
  createEntityAdapter,
  createSelector,
  EntityState,
} from '@reduxjs/toolkit';
import { apiSlice } from './api';
import { ListParams, ResponseBody } from '../types/common';
import { Library, LibraryInit } from '../types/library';
import { RootState } from './store';

const libraryAdapter = createEntityAdapter({
  selectId: (data: Library) => data.project.projectId,
});
const initialState = libraryAdapter.getInitialState();

export const libApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLibrary: builder.query<EntityState<Library>, ListParams>({
      query: ({
        page = 0,
        search = '',
        size = 24,
        sortBy = 'reg_date',
        sortDir = 'DESC',
      }) => ({
        url: 'personal/detail/projects',
        params: { page, search, size, sortBy, sortDir },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.ids.push(...newItems.ids);
          currentCache.ids = [...new Set(currentCache.ids)];
          currentCache.entities = {
            ...currentCache.entities,
            ...newItems.entities,
          };
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) =>
        libraryAdapter.setAll(initialState, res.data),
      providesTags: (result, error, arg) => {
        return result
          ? [
              'Library',
              ...result.ids.map((id) => ({
                type: 'Library' as const,
                id,
              })),
            ]
          : ['Library'];
      },
    }),
    getLibProject: builder.query({
      query: (projectId) => `personal/detail/projects/${projectId}`,
    }),
    uploadLibFile: builder.mutation({
      query: (formData) => ({
        url: `personal/upload`,
        method: 'POST',
        body: formData,
      }),
    }),
    updateCover: builder.mutation({
      query: ({ projectId, formData }) => ({
        url: `personal/detail/projects/${projectId}/cover`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { projectId }) => ['Main'],
    }),
    delProject: builder.mutation({
      query: (data) => ({
        url: 'library/personal/edit/projects/delete',
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: (result, error, arg) => {
        return ['Main', 'Search', 'Trash'];
      },
    }),
    updateBookInfo: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `personal/detail/projects/${projectId}/project`,
        method: 'PUT',
        body: { data },
      }),
    }),
    getIndex: builder.query({
      query: (projectId) => `personal/detail/projects/${projectId}/index`,
      transformResponse: (res: ResponseBody) => res.data.indexList,
    }),
    updateIndex: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `personal/detail/projects/${projectId}/index`,
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Library', id: projectId },
      ],
    }),
    updateNote: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `personal/detail/projects/${projectId}/note`,
        method: 'PUT',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Library', id: projectId },
      ],
    }),
    updateFileName: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `personal/detail/projects/${projectId}/resource`,
        method: 'PUT',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Library', id: projectId },
      ],
    }),
    getNewLink: builder.query({
      query: (projectId) =>
        `library/personal/detail/projects/${projectId}/share/link/new`,
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getSharedLink: builder.query({
      query: (projectId) =>
        `library/personal/detail/projects/${projectId}/share/link`,
      transformResponse: (res: ResponseBody) => res.data,
    }),
    updateShareLink: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `library/personal/detail/projects/${projectId}/share/link`,
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Library', id: projectId },
      ],
    }),
    deleteShareLink: builder.mutation({
      query: (projectId) => ({
        url: `library/personal/detail/projects/${projectId}/share/link`,
        method: 'DELETE',
      }),
    }),
    getSharedUsers: builder.query({
      query: (projectId) => ({
        url: `library/personal/detail/projects/${projectId}/share/users`,
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getSearchUsers: builder.query({
      query: ({ projectId, search }) => ({
        url: `library/personal/detail/projects/${projectId}/share/users/search`,
        params: { search },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    deleteSharedUsers: builder.mutation({
      query: (projectId) => ({
        url: `library/personal/detail/projects/${projectId}/share/users`,
        method: 'DELETE',
      }),
    }),
    validateUsers: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `library/personal/detail/projects/${projectId}/share/users/validate`,
        method: 'POST',
        body: { data },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    updateShareUser: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `library/personal/detail/projects/${projectId}/share/users`,
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Library', id: projectId },
      ],
    }),
    updatePublic: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `personal/detail/projects/${projectId}/public`,
        method: 'PUT',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Library', id: projectId },
      ],
    }),
    getLibHistory: builder.query({
      query: (projectId) => `personal/detail/projects/${projectId}/history`,
      providesTags: ['Library'],
    }),
    delLibHistory: builder.mutation({
      query: ({ projectId }) => ({
        url: `personal/detail/projects/${projectId}/history`,
        method: 'DELETE',
      }),
    }),
    bindPdf: builder.mutation({
      query: (resourceId) => ({
        url: 'library/resources/bind/pdf',
        method: 'POST',
        body: { data: { resourceId } },
      }),
    }),
    bindCover: builder.mutation({
      query: ({ projectId, resourceId }) => ({
        url: 'library/resources/bind/cover',
        method: 'POST',
        body: { data: { projectId, resourceId } },
      }),
    }),
    bindNewCover: builder.mutation({
      query: ({ projectId, resourceId }) => ({
        url: 'library/resources/bind/cover-new',
        method: 'POST',
        body: { data: { projectId, resourceId } },
      }),
    }),
    bindPage: builder.mutation({
      query: (data) => ({
        url: 'library/resources/bind/page',
        method: 'POST',
        body: { data },
      }),
    }),
    bindThumbnail: builder.mutation({
      query: (data) => ({
        url: 'library/resources/bind/thumbnail',
        method: 'POST',
        body: { data },
      }),
    }),
    deleteLibResources: builder.mutation({
      query: (projectId) => ({
        url: `library/resources/${projectId}`,
        method: 'DELETE',
      }),
    }),
    bindConvert: builder.mutation({
      query: (resourceId) => ({
        url: 'library/resources/bind/convert',
        method: 'POST',
        body: { data: { resourceId } },
      }),
    }),
    finishUpload: builder.mutation({
      query: (projectId) => ({
        url: 'library/resources/upload/finish',
        method: 'POST',
        body: { data: { projectId } },
      }),
    }),
    libraryInit: builder.query<LibraryInit, void>({
      query: () => ({
        url: 'library/personal/init',
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    updateCopyright: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `library/personal/detail/projects/${projectId}/copyright`,
        method: 'PUT',
        body: { data },
      }),
    }),
  }),
});

export const {
  useGetLibraryQuery,
  useGetLibProjectQuery,
  useUploadLibFileMutation,
  useUpdateCoverMutation,
  useDelProjectMutation,
  useUpdateBookInfoMutation,
  useGetIndexQuery,
  useUpdateIndexMutation,
  useUpdateNoteMutation,
  useUpdateFileNameMutation,
  useGetSharedLinkQuery,
  useGetNewLinkQuery,
  useUpdateShareLinkMutation,
  useGetSharedUsersQuery,
  useGetSearchUsersQuery,
  useDeleteShareLinkMutation,
  useDeleteSharedUsersMutation,
  useValidateUsersMutation,
  useUpdateShareUserMutation,
  useUpdatePublicMutation,
  useGetLibHistoryQuery,
  useDelLibHistoryMutation,
  useBindPdfMutation,
  useBindCoverMutation,
  useBindNewCoverMutation,
  useBindPageMutation,
  useBindThumbnailMutation,
  useDeleteLibResourcesMutation,
  useBindConvertMutation,
  useFinishUploadMutation,
  useLibraryInitQuery,
  useUpdateCopyrightMutation,
} = libApiSlice;

export const selectLibraryResult = libApiSlice.endpoints.getLibrary.select({
  page: 0,
});

const selectLibraryData = createSelector(
  selectLibraryResult,
  (libraryResult) => libraryResult.data
);

export const {
  selectAll: selectAllLibProjects,
  selectById: selectProjectById,
  selectIds: selectProjectIds,
  selectTotal: selectTotalProjectsNum,
} = libraryAdapter.getSelectors(
  (state: RootState) => selectLibraryData(state) ?? initialState
);
